// src/modals/OpportunityContactsModal.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';

/**
 * OpportunityContactsModal displays a detailed view of all contacts for an opportunity
 */
const OpportunityContactsModal = ({
  open,
  onClose,
  contacts = [],
  clientName,
  opportunityName,
  primaryContact = null
}) => {
  // Get color based on Revi score
  const getReviScoreColor = (score) => {
    if (score === null || score === undefined) return '#757575'; // Gray for N/A
    if (score >= 7.5) return '#03DAC6';  // Green for great
    if (score >= 4) return '#FFCF44'; // Yellow for good
    return '#CF6679'; // Red for poor
  };

  // Handler for email button
  const handleEmail = (email) => {
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };
  
  // Handler for phone button
  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#121212',
          color: '#fff',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: '#1A1A1A',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <Box>
          <Typography variant="h6" component="div">
            Client Contacts
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {clientName} - {opportunityName}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2, bgcolor: '#121212' }}>
        {contacts.length > 0 ? (
          <List sx={{ width: '100%' }}>
            {contacts.map((contact, index) => {
              const isPrimary = primaryContact && 
                (primaryContact.email === contact.email || 
                 primaryContact.name === contact.name);
              
              return (
                <React.Fragment key={index}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      bgcolor: isPrimary ? 'rgba(3, 218, 198, 0.1)' : 'transparent',
                      borderRadius: 1,
                      p: 2
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PersonIcon sx={{ color: isPrimary ? '#03DAC6' : 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {contact.name || 
                             contact.full_name || 
                             (contact.first_name && contact.last_name ? 
                               `${contact.first_name} ${contact.last_name}` : 
                               'Unknown Contact')}
                          </Typography>
                          {isPrimary && (
                            <Chip 
                              label="Primary" 
                              size="small" 
                              sx={{ 
                                bgcolor: 'rgba(3, 218, 198, 0.2)',
                                color: '#03DAC6',
                                height: 20,
                                fontSize: '0.65rem'
                              }} 
                            />
                          )}
                          {typeof contact.revi_score === 'number' && (
                            <Chip 
                              icon={<StarIcon sx={{ fontSize: '0.8rem !important' }} />}
                              label={`Revi: ${contact.revi_score}`}
                              size="small" 
                              sx={{ 
                                bgcolor: `${getReviScoreColor(contact.revi_score)}20`,
                                color: getReviScoreColor(contact.revi_score),
                                height: 20,
                                fontSize: '0.65rem'
                              }} 
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {contact.title && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {contact.title}
                            </Typography>
                          )}
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {contact.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon 
                                  sx={{ 
                                    fontSize: '0.9rem', 
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': { color: '#64B5F6' }
                                  }} 
                                  onClick={() => handleEmail(contact.email)}
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    '&:hover': { color: '#64B5F6' }
                                  }}
                                  onClick={() => handleEmail(contact.email)}
                                >
                                  {contact.email}
                                </Typography>
                                <Button 
                                  variant="outlined" 
                                  size="small"
                                  onClick={() => handleEmail(contact.email)}
                                  sx={{ 
                                    ml: 'auto', 
                                    fontSize: '0.7rem',
                                    py: 0.25,
                                    minWidth: 0,
                                    color: '#64B5F6',
                                    borderColor: 'rgba(100, 181, 246, 0.3)'
                                  }}
                                >
                                  Email
                                </Button>
                              </Box>
                            )}
                            
                            {contact.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon 
                                  sx={{ 
                                    fontSize: '0.9rem', 
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    '&:hover': { color: '#03DAC6' }
                                  }} 
                                  onClick={() => handleCall(contact.phone)}
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    '&:hover': { color: '#03DAC6' }
                                  }}
                                  onClick={() => handleCall(contact.phone)}
                                >
                                  {contact.phone}
                                </Typography>
                                <Button 
                                  variant="outlined" 
                                  size="small"
                                  onClick={() => handleCall(contact.phone)}
                                  sx={{ 
                                    ml: 'auto', 
                                    fontSize: '0.7rem',
                                    py: 0.25,
                                    minWidth: 0,
                                    color: '#03DAC6',
                                    borderColor: 'rgba(3, 218, 198, 0.3)'
                                  }}
                                >
                                  Call
                                </Button>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No contacts available for this opportunity.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

OpportunityContactsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contacts: PropTypes.array,
  clientName: PropTypes.string,
  opportunityName: PropTypes.string,
  primaryContact: PropTypes.object
};

export default OpportunityContactsModal;
